// Web-only boundary that re-portals react-aria overlays (popovers, menus,
// tooltips) into a container rendered inside this subtree instead of
// document.body.
//
// Purpose: react-aria's FocusScope decides inside-vs-outside primarily by DOM
// containment, so overlays portaled to document.body under a containing trap
// (FocusLoopView) lose a timing race on open — the trap steals focus back,
// the overlay's isOpen oscillates, and it flickers shut. Rendering the portal
// container inside the trapped subtree makes containment structural: the
// steal branches never run, and Tab containment extends to the overlays.
//
// The container div is positioned with a high z-index (react-aria overlays
// carry none and would otherwise paint under sticky headers) and rendered
// last so overlays stack above siblings. getContainer returns null on the
// very first render (before the ref attaches); react-aria renders nothing for
// that single commit, which only matters for overlays that mount already-open.
//
// IMPORTANT: the provider must be imported from the 'react-aria/PortalProvider'
// subpath, NOT the top-level 'react-aria' entry. Metro resolves the top-level
// entry via the package's exports map (the ".mjs" build) but resolves subpath
// imports like react-aria-components' internal "react-aria/Overlay" via the
// legacy per-subpath directory stubs (the ".js" build) — two separate module
// instances with two separate PortalContext objects. A provider from the
// top-level entry is invisible to the Overlay and silently no-ops (overlays
// keep portaling to document.body and the trap flickers them shut). The
// subpath import uses the same resolution mechanism as the Overlay, so they
// share one context under any resolver behavior.
import React, { useState } from 'react';
import { UNSAFE_PortalProvider } from 'react-aria/PortalProvider';

export function OverlayPortalBoundary({ children }: { children: React.ReactNode }) {
    const [container, setContainer] = useState<HTMLElement | null>(null);

    return (
        <>
            <UNSAFE_PortalProvider getContainer={() => container}>{children}</UNSAFE_PortalProvider>
            <div ref={setContainer} style={{ position: 'relative', zIndex: 1000 }} />
        </>
    );
}
